// Platform Admin — Yearly Revenue Projection Tile
// Shows real-time projected ARR based on founding + standard schools × (real users OR fallback)
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RevenueProjectionTile({ token, language = 'hi' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultUsers, setDefaultUsers] = useState(200);

  const load = async (users = defaultUsers) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/platform-admin/pricing/revenue/projection?default_users_per_school=${users}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) setData(d);
    } catch (e) { console.warn('revenue projection:', e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading && !data) return <div className="bg-white border rounded-xl p-5 text-sm text-gray-500">Loading projection…</div>;
  if (!data) return null;

  const sym = data.currency_symbol;
  const fmt = (n) => `${sym}${Number(n || 0).toLocaleString('en-IN')}`;
  const t = data.totals || {};
  const c = data.counts || {};

  return (
    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white shadow-lg" data-testid="revenue-projection-tile">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs text-white/80 font-semibold uppercase tracking-wider">
            {language === 'hi' ? '💰 Year-1 Projected Revenue' : '💰 Year-1 Projected Revenue'}
          </p>
          <h3 className="text-4xl font-black mt-1" style={{ fontFamily: 'Outfit' }} data-testid="projected-arr-value">
            {fmt(t.year1_revenue)}
          </h3>
          <p className="text-xs text-white/70 mt-1">
            {language === 'hi' ? 'Recurring' : 'Recurring'} {fmt(t.annual_maintenance_arr)}/{language === 'hi' ? 'साल' : 'yr'}
            {' '}≈ {fmt(t.projected_monthly_recurring)}/{language === 'hi' ? 'महीना' : 'month'}
            {' '}({t.total_schools} schools · {t.total_users} users)
          </p>
        </div>
        <button onClick={() => load()} className="text-white/80 hover:text-white text-lg" title="Refresh" data-testid="refresh-projection-btn">↻</button>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
          <p className="text-[10px] text-white/70 uppercase font-semibold">{language === 'hi' ? 'One-time (पहला साल)' : 'One-time (first year)'}</p>
          <p className="text-xl font-bold" data-testid="onetime-total">{fmt(t.one_time_total)}</p>
          <p className="text-[10px] text-white/70 mt-1">{language === 'hi' ? 'नए schools का license+setup' : 'license+setup, new schools'}</p>
        </div>
        <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
          <p className="text-[10px] text-white/70 uppercase font-semibold">{language === 'hi' ? 'Recurring Maintenance/साल' : 'Recurring Maintenance/yr'}</p>
          <p className="text-xl font-bold" data-testid="maintenance-arr">{fmt(t.annual_maintenance_arr)}</p>
          <p className="text-[10px] text-white/70 mt-1">{t.custom_schools > 0 ? `+ ${t.custom_schools} custom (5000+ users) — sales quote` : (language === 'hi' ? 'slab-आधारित flat' : 'slab-based flat')}</p>
        </div>
      </div>

      {/* Assumption tweak */}
      <div className="mt-4 bg-white/10 rounded-lg p-3 flex items-center gap-3">
        <label className="text-xs text-white/80 whitespace-nowrap">
          {language === 'hi' ? 'Fallback users/school:' : 'Fallback users/school:'}
        </label>
        <input
          type="number"
          min="1"
          max="100000"
          value={defaultUsers}
          onChange={(e) => setDefaultUsers(Math.max(1, Number(e.target.value) || 1))}
          onBlur={() => load()}
          className="bg-white/20 border border-white/30 rounded-md px-2 py-1 text-sm font-semibold w-24 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
          data-testid="fallback-users-input"
        />
        <span className="text-[10px] text-white/60">
          ({c.schools_from_real_data || 0} real · {c.schools_from_fallback || 0} fallback)
        </span>
      </div>

      <p className="text-[10px] text-white/60 mt-3 italic">
        {language === 'hi'
          ? 'Slab model: flat one-time + सालाना maintenance, कुल users (छात्र+staff) पर। 5000+ = custom (totals से बाहर)।'
          : 'Slab model: flat one-time + yearly maintenance, by total users (students+staff). 5000+ = custom (excluded).'}
      </p>
    </div>
  );
}
